import { Button, IDatePickerPayload, Icon, Theme } from '@busybusy/webapp-react-ui';
import { MemberPermissionFilter, PositionFilter } from '__generated__/graphql';
import { FunnelIcon } from 'assets/icons';
import classNames from 'classnames';
import EmployeeCheckList from 'components/domain/member/EmployeeCheckList/EmployeeCheckList';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import { useOpenable } from 'hooks';
import {
  useEmployeePickerFilterCertification,
  useEmployeePickerFilterPosition,
  useEmployeePickerFilters,
} from 'hooks/feature/picker/employee/employeePickerFilter/useEmployeePickerFilter';
import { useCertificationRecordsQuery } from 'hooks/models/certification/useCertificationRecordsQuery/useCertificationRecordsQuery';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { getGraphQlEqualComparison } from 'utils/apolloUtils';
import MultiPicker from '../../../foundation/pickers/MultiPicker/MultiPicker';
import EmployeePicker from '../EmployeePicker/EmployeePicker';
import {
  EmployeeSmartFilterForm,
  type IEmployeeSmartFilterFieldOptions,
  type IEmployeeSmartFilterFormData,
} from '../EmployeeSmartFilterForm/EmployeeSmartFilterForm';
import './EmployeeMultiPicker.scss';

export interface IEmployeeMultiPickerProps {
  readonly values: string[];
  permissions?: MemberPermissionFilter;
  blacklistedIds?: string[];
  filterAlreadySelectedByBlacklist?: boolean;
  onChange: (values: string[]) => void;
  error?: boolean;
  placeholder?: string;
  className?: ClassName;
  includeClockedIn?: boolean;
  showSmartFilter?: boolean;
  positionFilter?: PositionFilter;
}

export interface IClockedInFilter {
  clockedInProjectId: string | null;
  clockedInCostCodeId: string | null;
  clockedInEquipmentId: string | null;
}

export interface IScheduledFilter {
  scheduledProjectId: string | null;
  scheduledCostCodeId: string | null;
  scheduledEquipmentId: string | null;
  scheduledDateRestrictions: string | null;
  scheduledDate: IDatePickerPayload | null;
}

export const defaultEmployeeMultiPickerFilterOptions: IEmployeeSmartFilterFieldOptions = {
  smartClockedInFilter: false,
  smartScheduledFilter: false,
  smartClockedOutFilter: false,
  positionFilter: true,
  certificationFilter: false,
};

export const defaultEmployeeMultiPickerSmartFilterOptions: IEmployeeSmartFilterFieldOptions = {
  ...defaultEmployeeMultiPickerFilterOptions,
  smartClockedInFilter: true,
  smartScheduledFilter: true,
  smartClockedOutFilter: true,
};

export const employeeSmartFilterFormDefaultData: IEmployeeSmartFilterFormData = {
  showClockedIn: false,
  showClockedOut: false,
  showScheduled: false,
  clockedInProjectId: null,
  clockedInCostCodeId: null,
  clockedInEquipmentId: null,
  scheduledProjectId: null,
  scheduledCostCodeId: null,
  scheduledEquipmentId: null,
  scheduledDateRestrictions: null,
  scheduledDate: null,
  positionId: null,
  certificationId: null,
};

const EmployeeMultiPicker = ({
  permissions,
  onChange,
  blacklistedIds,
  filterAlreadySelectedByBlacklist = true,
  values,
  error,
  placeholder,
  className,
  includeClockedIn,
  showSmartFilter,
  positionFilter,
}: IEmployeeMultiPickerProps) => {
  const checkListDialogDetails = useOpenable();
  const smartFilterDialog = useOpenable();
  const enableEmployeePickerFilters = useEmployeePickerFilters();
  const enableEmployeePickerFilterPosition = useEmployeePickerFilterPosition();
  const enableEmployeePickerFilterCertification = useEmployeePickerFilterCertification();

  const [employeeSmartFilterData, setEmployeeSmartFilterData] = useState<IEmployeeSmartFilterFormData>(
    employeeSmartFilterFormDefaultData
  );

  const certificationRecordsQuery = useCertificationRecordsQuery({
    variables: {
      filter: {
        certificationId: getGraphQlEqualComparison(employeeSmartFilterData.certificationId),
      },
    },
    options: {
      enabled: !!employeeSmartFilterData.certificationId,
    },
  });

  const allowedIds: string[] | undefined = useMemo(() => {
    if (!enableEmployeePickerFilterCertification || !employeeSmartFilterData.certificationId) {
      return undefined;
    }

    // allow none while generating allow list
    if (certificationRecordsQuery.isLoading) {
      return [];
    }

    return certificationRecordsQuery.data?.map((record) => record.memberId) || [];
  }, [
    enableEmployeePickerFilterCertification,
    employeeSmartFilterData.certificationId,
    certificationRecordsQuery.data,
    certificationRecordsQuery.isLoading,
  ]);

  const filterOptions: IEmployeeSmartFilterFieldOptions = useMemo(
    () => ({
      ...(showSmartFilter ? defaultEmployeeMultiPickerSmartFilterOptions : defaultEmployeeMultiPickerFilterOptions),
      positionFilter: enableEmployeePickerFilterPosition,
      certificationFilter: enableEmployeePickerFilterCertification,
    }),
    [showSmartFilter, enableEmployeePickerFilterPosition, enableEmployeePickerFilterCertification]
  );

  const allowFilters = useMemo(
    () => showSmartFilter || enableEmployeePickerFilters,
    [showSmartFilter, enableEmployeePickerFilters]
  );

  const hasFilters = useMemo(
    () =>
      allowFilters &&
      (employeeSmartFilterData.showClockedIn ||
        employeeSmartFilterData.showClockedOut ||
        employeeSmartFilterData.showScheduled ||
        employeeSmartFilterData.positionId ||
        employeeSmartFilterData.certificationId),
    [
      allowFilters,
      employeeSmartFilterData.showClockedIn,
      employeeSmartFilterData.showClockedOut,
      employeeSmartFilterData.showScheduled,
      employeeSmartFilterData.positionId,
      employeeSmartFilterData.certificationId,
    ]
  );

  function getEmployeeSmartFilterData() {
    return employeeSmartFilterData;
  }

  function handleClearEmployeeSmartFilterData() {
    setEmployeeSmartFilterData({ ...employeeSmartFilterFormDefaultData });
  }

  function handleOnSubmitSmartFilter(data: IEmployeeSmartFilterFormData) {
    setEmployeeSmartFilterData(data);

    smartFilterDialog.close();
  }

  function onEmployeeCheckListSubmit(memberIds: string[]) {
    checkListDialogDetails.close();
    onChange(memberIds);
  }

  function handleOnChange(newValues: string[]) {
    if (values.length > 1) {
      onChange(newValues.filter((value) => value !== null));
    } else {
      onChange(newValues);
    }
  }

  function renderItem(value: string | null, index: number) {
    const onMemberSelect = (id: string | null) => {
      if (index === 0 || index < values.length) {
        if (id !== null) {
          const newValues = [...values];
          newValues[index] = id;
          handleOnChange(newValues);
        } else {
          handleOnChange(values.filter((_v, i) => i !== index));
        }
      }
    };

    return (
      <EmployeePicker
        className={index !== 0 ? 'mt-4' : ''}
        blacklistedIds={blacklistedIds ? values.concat(blacklistedIds) : values}
        permissions={permissions}
        key={value ? value : 'null'}
        error={error}
        value={value}
        onSelect={onMemberSelect}
        placeholder={placeholder}
        includeClockedIn={includeClockedIn}
        positionFilter={positionFilter}
      />
    );
  }

  const rightContent = useMemo(() => {
    if (!allowFilters) {
      return null;
    }

    const btnClasses = classNames('filter-button', {
      'icon-status-indicator': hasFilters,
      'icon-status-indicator-danger': hasFilters,
    });
    return (
      <Button type="icon" onClick={smartFilterDialog.open} className={btnClasses}>
        <Icon svg={FunnelIcon} theme={Theme.DARK} />
      </Button>
    );
  }, [allowFilters, hasFilters, smartFilterDialog.open]);

  const filteredValues =
    filterAlreadySelectedByBlacklist && blacklistedIds
      ? values.filter((value) => !blacklistedIds.some((blacklistId) => blacklistId === value))
      : values;

  const combinedPositionFilter = useMemo(() => {
    if (!positionFilter && !employeeSmartFilterData.positionId) {
      return undefined;
    }

    return {
      ...positionFilter,
      ...(employeeSmartFilterData.positionId
        ? { id: getGraphQlEqualComparison(employeeSmartFilterData.positionId) }
        : {}),
    };
  }, [positionFilter, employeeSmartFilterData.positionId]);

  const classes = classNames('employee-multi-picker', className);

  return (
    <div className={classes}>
      <HeaderDialog
        className="employee-multi-picker-dialog"
        title={t('Select Employees')}
        isOpen={checkListDialogDetails.isOpen}
        onClose={checkListDialogDetails.close}
        rightContent={rightContent}
      >
        <EmployeeCheckList
          checkedItems={filteredValues}
          enableGroups={true}
          enableSearch={true}
          onSubmit={onEmployeeCheckListSubmit}
          permissions={permissions}
          blacklistedIds={blacklistedIds}
          allowedIds={allowedIds}
          positionFilter={combinedPositionFilter}
          includeClockedIn={includeClockedIn || employeeSmartFilterData.showClockedIn}
          includeClockedOut={employeeSmartFilterData.showClockedOut}
          includeScheduled={employeeSmartFilterData.showScheduled}
          clockedInFilter={
            employeeSmartFilterData.showClockedIn
              ? {
                  clockedInCostCodeId: employeeSmartFilterData.clockedInCostCodeId,
                  clockedInEquipmentId: employeeSmartFilterData.clockedInEquipmentId,
                  clockedInProjectId: employeeSmartFilterData.clockedInProjectId,
                }
              : undefined
          }
          scheduledFilter={
            employeeSmartFilterData.showScheduled
              ? {
                  scheduledCostCodeId: employeeSmartFilterData.scheduledCostCodeId,
                  scheduledEquipmentId: employeeSmartFilterData.scheduledEquipmentId,
                  scheduledProjectId: employeeSmartFilterData.scheduledProjectId,
                  scheduledDateRestrictions: employeeSmartFilterData.scheduledDateRestrictions,
                  scheduledDate: employeeSmartFilterData.scheduledDate,
                }
              : undefined
          }
        />
      </HeaderDialog>
      <MultiPicker
        onAddClick={checkListDialogDetails.open}
        limit={3}
        renderItem={renderItem}
        // Null list item so there's an empty employee picker as a default
        values={filteredValues && filteredValues.length ? filteredValues : [null]}
      />
      <HeaderDialog
        title={enableEmployeePickerFilters ? t('Employee Filter') : t('Smart Filters')}
        isOpen={smartFilterDialog.isOpen}
        onClose={smartFilterDialog.close}
      >
        <EmployeeSmartFilterForm
          filterOptions={filterOptions}
          getFormData={getEmployeeSmartFilterData}
          onFormSubmit={handleOnSubmitSmartFilter}
          buttonTitle={t('Submit')}
          onClearFilters={handleClearEmployeeSmartFilterData}
          className={'p-5'}
        />
      </HeaderDialog>
    </div>
  );
};

export default EmployeeMultiPicker;
